import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import './Toast.css';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
    id: number;
    kind: ToastKind;
    message: string;
}

interface ToastApi {
    toast: (message: string, kind?: ToastKind) => void;
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const ICONS: Record<ToastKind, ReactNode> = {
    success: <CheckCircle2 size={18} />,
    error: <XCircle size={18} />,
    info: <Info size={18} />,
    warning: <AlertTriangle size={18} />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
    const [items, setItems] = useState<ToastItem[]>([]);
    const counter = useRef(0);

    const dismiss = useCallback((id: number) => {
        setItems(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = useCallback((message: string, kind: ToastKind = 'info') => {
        const id = ++counter.current;
        setItems(prev => [...prev.slice(-3), { id, kind, message }]);
        window.setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4000);
    }, [dismiss]);

    const api = useMemo<ToastApi>(() => ({
        toast,
        success: (m) => toast(m, 'success'),
        error: (m) => toast(m, 'error'),
        info: (m) => toast(m, 'info'),
        warning: (m) => toast(m, 'warning'),
    }), [toast]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className="toast-region" role="status" aria-live="polite">
                {items.map(t => (
                    <div key={t.id} className={`toast toast-${t.kind}`}>
                        <span className="toast-icon">{ICONS[t.kind]}</span>
                        <span className="toast-message">{t.message}</span>
                        <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
}
